import type {LocalDate} from '../domain/local-date.value-object';
import type {ScheduleCalendarRange} from '../domain/schedule-calendar';
import {addDays, addMonths, addYears, monthGridRange, weekRange, yearRange} from './date';

const WEEK_STEP_DAYS = 7;

export const SCHEDULE_CALENDAR_VIEWS = ['day', 'week', 'month', 'year'] as const;

export type ScheduleCalendarView = (typeof SCHEDULE_CALENDAR_VIEWS)[number];

/** A view plus the date it is anchored on: everything the calendar search carries. */
export type ScheduleCalendarTarget = {
  readonly view: ScheduleCalendarView;
  readonly anchor: LocalDate;
};

/** The first load opens on the current week. */
export const DEFAULT_SCHEDULE_CALENDAR_VIEW: ScheduleCalendarView = 'week';

export const isScheduleCalendarView = (value: string): value is ScheduleCalendarView =>
  SCHEDULE_CALENDAR_VIEWS.some(view => view === value);

/**
 * The window each view asks the calendar endpoint for. Day view reads its month grid:
 * the mini-month beside the list needs it, and it shares month view's cache entry.
 */
const RANGE_BY_VIEW: Readonly<
  Record<ScheduleCalendarView, (anchor: LocalDate) => ScheduleCalendarRange>
> = {
  day: monthGridRange,
  week: weekRange,
  month: monthGridRange,
  year: yearRange,
};

const STEP_BY_VIEW: Readonly<
  Record<ScheduleCalendarView, (anchor: LocalDate, direction: -1 | 1) => LocalDate>
> = {
  day: (anchor, direction) => addDays(anchor, direction),
  week: (anchor, direction) => addDays(anchor, direction * WEEK_STEP_DAYS),
  month: (anchor, direction) => addMonths(anchor, direction),
  year: (anchor, direction) => addYears(anchor, direction),
};

/** How a view names itself ("Month view") and its paging unit ("Previous month"). */
export const SCHEDULE_CALENDAR_VIEW_COPY: Readonly<
  Record<ScheduleCalendarView, {readonly name: string; readonly period: string}>
> = {
  day: {name: 'Day', period: 'day'},
  week: {name: 'Week', period: 'week'},
  month: {name: 'Month', period: 'month'},
  year: {name: 'Year', period: 'year'},
};

export const calendarRangeFor = (
  view: ScheduleCalendarView,
  anchor: LocalDate,
): ScheduleCalendarRange => RANGE_BY_VIEW[view](anchor);

/** Previous / Next moves one day, week, month, or year, following the view. */
export const stepCalendarAnchor = (
  view: ScheduleCalendarView,
  anchor: LocalDate,
  direction: -1 | 1,
): LocalDate => STEP_BY_VIEW[view](anchor, direction);
